import type {
  AdvancedSearchQuery,
  FieldOperator,
  LogicalOperator,
} from '../../components/TableSearch/types';

/**
 * A named filter preset for the products DataGrid.
 * Built-in presets are locked (not editable or deletable by users).
 * User-saved presets are stored in localStorage under STORAGE_KEY_FILTER_PRESETS.
 */
export interface FilterPreset {
  name: string;
  query: AdvancedSearchQuery;
  isBuiltIn: boolean;
}

/**
 * Code-embedded built-in filter presets.
 * These are never written to localStorage — defined here as constants.
 *
 * Order matters: first entry is applied on first load when no localStorage preference exists.
 */
export const BUILT_IN_FILTER_PRESETS: FilterPreset[] = [
  {
    name: 'Orange Book Active',
    isBuiltIn: true,
    query: {
      filterGroups: [
        {
          id: 'preset-fda-active',
          logicalOperator: 'AND' as LogicalOperator,
          rules: [
            {
              id: 'rule-fda-active-1',
              field: 'is_fda_linked',
              operator: 'equals' as FieldOperator,
              // String 'true' — backend handles both boolean true and string 'true'
              // (see advancedSearchBuilder.js addRuleParameter boolean branch)
              value: 'true',
            },
          ],
        },
      ],
      groupOperator: 'AND' as LogicalOperator,
    },
  },
  {
    name: 'Has No External Links',
    isBuiltIn: true,
    query: {
      filterGroups: [
        {
          id: 'preset-no-external-links',
          logicalOperator: 'AND' as LogicalOperator,
          rules: [
            {
              id: 'rule-no-external-links-cas',
              field: 'is_cas_verified',
              operator: 'equals' as FieldOperator,
              value: 'false',
            },
            {
              id: 'rule-no-external-links-ob',
              field: 'is_fda_linked',
              operator: 'equals' as FieldOperator,
              value: 'false',
            },
            {
              id: 'rule-no-external-links-ndc',
              field: 'is_ndc_linked',
              operator: 'equals' as FieldOperator,
              value: 'false',
            },
            {
              id: 'rule-no-external-links-dmf',
              field: 'is_dmf_linked',
              operator: 'equals' as FieldOperator,
              value: 'false',
            },
          ],
        },
      ],
      groupOperator: 'AND' as LogicalOperator,
    },
  },
  {
    name: 'Has All External Links',
    isBuiltIn: true,
    query: {
      filterGroups: [
        {
          id: 'preset-all-external-links',
          logicalOperator: 'AND' as LogicalOperator,
          rules: [
            {
              id: 'rule-all-external-links-cas',
              field: 'is_cas_verified',
              operator: 'equals' as FieldOperator,
              value: 'true',
            },
            {
              id: 'rule-all-external-links-ob',
              field: 'is_fda_linked',
              operator: 'equals' as FieldOperator,
              value: 'true',
            },
            {
              id: 'rule-all-external-links-ndc',
              field: 'is_ndc_linked',
              operator: 'equals' as FieldOperator,
              value: 'true',
            },
            {
              id: 'rule-all-external-links-dmf',
              field: 'is_dmf_linked',
              operator: 'equals' as FieldOperator,
              value: 'true',
            },
          ],
        },
      ],
      groupOperator: 'AND' as LogicalOperator,
    },
  },
  {
    name: 'All Products',
    isBuiltIn: true,
    // Empty filterGroups = no filter applied (show all products)
    query: {
      filterGroups: [],
      groupOperator: 'AND' as LogicalOperator,
    },
  },
];
